import React from "react";
import { Link } from "react-router-dom";
import SEO from "../../common/SEO";
import Layout from "../../common/Layout";
import FooterOne from "../../common/footer/FooterOne";
import ContactUsForm from "../../components/contact/ContactUsForm";
import { useTranslation } from "react-i18next";

const ADDRESS = "2000 Szentendre, Rózsa utca 8.";
const PHONE_DISPLAY = "+36 (1) 300 91 05";
const PHONE_HREF = "tel:+3613009105";
const EMAIL_PARTNER = "partner@happysolutions.hu";
const EMAIL_INFO = "info@happysolutions.hu";
const MAP_EMBED = `https://www.google.com/maps?q=${encodeURIComponent(ADDRESS)}&output=embed`;
const MAP_LINK = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(ADDRESS)}`;

const STYLES = {
    secondary: {
        fontSize: 14,
        opacity: 0.7,
    },
    grid: {
        display: "grid",
        gridTemplateColumns: "minmax(0, 1.1fr) minmax(0, 1fr)",
        gap: "32px",
        alignItems: "stretch",
    },
    mapCard: {
        padding: 0,
        overflow: "hidden",
        display: "flex",
        flexDirection: "column",
    },
    mapWrap: {
        position: "relative",
        width: "100%",
        paddingBottom: "62%",
        background: "#0F172A",
    },
    mapIframe: {
        position: "absolute",
        inset: 0,
        width: "100%",
        height: "100%",
        border: 0,
    },
    mapMeta: {
        padding: "20px 24px",
        display: "flex",
        flexDirection: "column",
        gap: 6,
    },
    mapCta: {
        marginTop: 10,
        alignSelf: "flex-start",
    },
};

const ContactUs = (props) => {
    const { t } = useTranslation();

    if (props.modalMode) {
        return (
            <div className="premium-contact-modal" style={{ padding: "1rem 0" }}>
                <div className="premium-container">
                    <div className="premium-section-head text-center" style={{ marginBottom: 16 }}>
                        <span className="premium-eyebrow">{t("contactUs.preTitle", { defaultValue: "Kérdése van, netán elakadt?" })}</span>
                        <h2>{t("contactUs.title", { defaultValue: "Írjon nekünk üzenetet az alábbi űrlap kitöltésével!" })}</h2>
                    </div>
                    <ContactUsForm formStyle="rnt-contact-form rwt-dynamic-form row" modalMode={true} />
                </div>
            </div>
        );
    }

    const infoCards = [
        {
            icon: "ri-phone-line",
            title: t("contactUs.info.phoneTitle", { defaultValue: "Telefon" }),
            primary: <a href={PHONE_HREF}>{PHONE_DISPLAY}</a>,
            secondary: t("contactUs.info.phoneHours", { defaultValue: "H–P · 8:00 – 17:00" }),
        },
        {
            icon: "ri-mail-line",
            title: t("contactUs.info.emailTitle", { defaultValue: "E-mail" }),
            primary: <a href={`mailto:${EMAIL_INFO}`}>{EMAIL_INFO}</a>,
            secondary: <a href={`mailto:${EMAIL_PARTNER}`}>{EMAIL_PARTNER}</a>,
        },
        {
            icon: "ri-map-pin-2-line",
            title: t("contactUs.info.addressTitle", { defaultValue: "Központi iroda" }),
            primary: <a href={MAP_LINK} target="_blank" rel="noreferrer">{ADDRESS}</a>,
            secondary: t("contactUs.info.addressHint", { defaultValue: "Magyarország" }),
        },
        {
            icon: "ri-time-line",
            title: t("contactUs.info.hoursTitle", { defaultValue: "Ügyfélszolgálat" }),
            primary: t("contactUs.info.hoursPrimary", { defaultValue: "Hétfő – Péntek · 8:00 – 17:00" }),
            secondary: t("contactUs.info.hoursSecondary", { defaultValue: "Hétvégén ügyeleti rendben" }),
        },
    ];

    return (
        <>
            <SEO
                title={t("contactUs.seoTitle", { defaultValue: "Elérhetőségeink és kapcsolat" })}
                description={t("contactUs.seoDesc", { defaultValue: "Kérdése van, netán elakadt? Itt vagyunk elérhetőek." })}
            />
            <Layout>
                <section className="premium-product-hero">
                    <div className="premium-container premium-product-hero__grid">
                        <div className="premium-product-hero__content">
                            <span className="premium-hero__badge">
                                <i className="ri-customer-service-2-line" aria-hidden="true"></i>
                                {t("contactUs.eyebrow", { defaultValue: "Kapcsolat" })}
                            </span>
                            <h1>{t("contactUs.hero.title", { defaultValue: "Beszéljünk a következő projektedről." })}</h1>
                            <p>{t("contactUs.hero.lead", { defaultValue: "Írjon nekünk, hívjon bennünket vagy látogasson meg minket Szentendrén. Munkatársaink rövid időn belül felveszik Önnel a kapcsolatot." })}</p>
                            <div className="premium-chip-row premium-chip-row--large">
                                <a className="premium-chip premium-chip--solid" href={PHONE_HREF}>
                                    <i className="ri-phone-line" aria-hidden="true"></i> {PHONE_DISPLAY}
                                </a>
                                <a className="premium-chip premium-chip--solid" href={`mailto:${EMAIL_INFO}`}>
                                    <i className="ri-mail-line" aria-hidden="true"></i> {EMAIL_INFO}
                                </a>
                                <a className="premium-chip premium-chip--solid" href={MAP_LINK} target="_blank" rel="noreferrer">
                                    <i className="ri-map-pin-2-line" aria-hidden="true"></i> {ADDRESS}
                                </a>
                            </div>
                            <div className="premium-hero__actions">
                                <Link className="edu-btn" to="/foglalas">
                                    {t("contactUs.hero.ctaBook", { defaultValue: "Ingyenes konzultáció" })}
                                    <i className="icon-arrow-right-line-right"></i>
                                </Link>
                                <a className="edu-btn btn-secondary premium-btn-outline" href={PHONE_HREF}>
                                    <i className="ri-phone-line" aria-hidden="true"></i>
                                    {t("contactUs.hero.ctaCall", { defaultValue: "Hívjon minket" })}
                                </a>
                            </div>
                        </div>
                        <div className="premium-product-hero__visual">
                            <img
                                src="https://images.unsplash.com/photo-1556761175-5973dc0f32e7?q=80&w=1932&auto=format&fit=crop"
                                alt={t("contactUs.hero.title", { defaultValue: "Kapcsolat" })}
                                loading="eager"
                            />
                            <div className="premium-floating-card premium-floating-card--bottom">
                                <strong>{t("contactUs.hero.responseTitle", { defaultValue: "Gyors válasz" })}</strong>
                                <span>{t("contactUs.hero.responseDesc", { defaultValue: "általában 1 munkanapon belül" })}</span>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="premium-section">
                    <div className="premium-container">
                        <div className="premium-section-head premium-section-head--split">
                            <div>
                                <span className="premium-eyebrow">{t("contactUs.info.eyebrow", { defaultValue: "Elérhetőségek" })}</span>
                                <h2>{t("contactUs.info.title", { defaultValue: "Több csatornán is elérhetőek vagyunk." })}</h2>
                            </div>
                            <p>{t("contactUs.info.lead", { defaultValue: "Válassza azt a csatornát, ami a legkényelmesebb Önnek — kollégáink készséggel állnak rendelkezésére." })}</p>
                        </div>
                        <div className="premium-card-grid premium-card-grid--3">
                            {infoCards.map((card) => (
                                <div className="premium-feature-card" key={card.title}>
                                    <span className="premium-card-icon" aria-hidden="true"><i className={card.icon}></i></span>
                                    <h3>{card.title}</h3>
                                    <p style={{ marginBottom: 6 }}>{card.primary}</p>
                                    <p style={STYLES.secondary}>{card.secondary}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                <section className="premium-section premium-section--soft">
                    <div className="premium-container">
                        <div className="premium-section-head premium-section-head--split">
                            <div>
                                <span className="premium-eyebrow">{t("contactUs.form.eyebrow", { defaultValue: "Üzenet küldése" })}</span>
                                <h2>{t("contactUs.title", { defaultValue: "Írjon nekünk üzenetet az alábbi űrlap kitöltésével!" })}</h2>
                            </div>
                            <p>{t("contactUs.form.lead", { defaultValue: "Töltse ki a rövid űrlapot, és kollégáink hamarosan keresni fogják." })}</p>
                        </div>

                        <div
                            className="premium-contact-grid"
                            style={STYLES.grid}
                        >
                            <div className="premium-feature-card" style={{ padding: "32px" }}>
                                <ContactUsForm formStyle="rnt-contact-form rwt-dynamic-form row" />
                            </div>

                            <div className="premium-feature-card" style={STYLES.mapCard}>
                                <div style={STYLES.mapWrap}>
                                    <iframe
                                        title={t("contactUs.map.title", { defaultValue: "Happy Solutions iroda térképen" })}
                                        src={MAP_EMBED}
                                        style={STYLES.mapIframe}
                                        loading="lazy"
                                        referrerPolicy="no-referrer-when-downgrade"
                                        allowFullScreen
                                    />
                                </div>
                                <div style={STYLES.mapMeta}>
                                    <strong style={{ fontSize: 15 }}>
                                        <i className="ri-map-pin-2-line" aria-hidden="true" style={{ marginRight: 6 }}></i>
                                        Happy Solutions Kft.
                                    </strong>
                                    <a href={MAP_LINK} target="_blank" rel="noreferrer" style={{ fontSize: 14 }}>
                                        {ADDRESS}
                                    </a>
                                    <a
                                        href={MAP_LINK}
                                        target="_blank"
                                        rel="noreferrer"
                                        className="edu-btn btn-secondary premium-btn-outline"
                                        style={STYLES.mapCta}
                                    >
                                        {t("contactUs.map.cta", { defaultValue: "Útvonaltervezés" })}
                                        <i className="icon-arrow-right-line-right"></i>
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                <section className="premium-section premium-cta-band">
                    <div className="premium-container premium-cta-band__inner">
                        <div>
                            <span className="premium-eyebrow premium-eyebrow--light">{t("contactUs.cta.eyebrow", { defaultValue: "Demó és felmérés" })}</span>
                            <h2>{t("contactUs.cta.title", { defaultValue: "Ismerje meg személyesen is rendszereinket." })}</h2>
                            <p>{t("contactUs.cta.lead", { defaultValue: "Foglaljon időpontot egy ingyenes, online vagy személyes konzultációra, és megmutatjuk, hogyan illeszkednek a megoldásaink a folyamataihoz." })}</p>
                        </div>
                        <Link className="edu-btn" to="/foglalas">
                            {t("contactUs.cta.button", { defaultValue: "Konzultációt kérek" })}
                            <i className="icon-arrow-right-line-right"></i>
                        </Link>
                    </div>
                </section>
            </Layout>
            <FooterOne />

            <style>{`
                @media (max-width: 991px) {
                    .premium-contact-grid {
                        grid-template-columns: 1fr !important;
                    }
                }
            `}</style>
        </>
    );
};

export default ContactUs;
